import { useState, useEffect, useCallback } from 'react';
import type { Pin, CreatePinData, UpdatePinData } from '../types';
import * as pinService from '../services/pinService';

export interface UsePinsReturn {
  pins: Pin[];
  isLoading: boolean;
  error: string | null;
  selectedPin: Pin | null;
  selectPin: (pin: Pin | null) => void;
  addPin: (data: CreatePinData) => Promise<Pin>;
  editPin: (pinId: string, data: UpdatePinData) => Promise<Pin>;
  removePin: (pinId: string) => Promise<void>;
  refresh: () => Promise<void>;
}

/**
 * Hook that manages pin state and CRUD operations.
 * Fetches pins from the API and provides methods for creating, updating, and deleting pins.
 */
export function usePins(): UsePinsReturn {
  const [pins, setPins] = useState<Pin[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPin, setSelectedPin] = useState<Pin | null>(null);

  const loadPins = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await pinService.fetchPins();
      setPins(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load pins');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPins();
  }, [loadPins]);

  const selectPin = useCallback((pin: Pin | null) => {
    setSelectedPin(pin);
  }, []);

  const addPin = useCallback(async (data: CreatePinData): Promise<Pin> => {
    const newPin = await pinService.createPin(data);
    setPins((prev) => [...prev, newPin]);
    return newPin;
  }, []);

  const editPin = useCallback(async (pinId: string, data: UpdatePinData): Promise<Pin> => {
    const updated = await pinService.updatePin(pinId, data);
    setPins((prev) => prev.map((p) => (p.id === pinId ? updated : p)));
    if (selectedPin?.id === pinId) {
      setSelectedPin(updated);
    }
    return updated;
  }, [selectedPin]);

  const removePin = useCallback(async (pinId: string): Promise<void> => {
    await pinService.deletePin(pinId);
    setPins((prev) => prev.filter((p) => p.id !== pinId));
    if (selectedPin?.id === pinId) {
      setSelectedPin(null);
    }
  }, [selectedPin]);

  return {
    pins,
    isLoading,
    error,
    selectedPin,
    selectPin,
    addPin,
    editPin,
    removePin,
    refresh: loadPins,
  };
}
